import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  LinearProgress,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  Link
} from '@mui/material';
import SearchComponent from '../../components/common/SearchComponent';
import { fetchDeliveryRequests } from '../../services/dataService';
import { searchProjects } from '../../services/searchService';
import api from '../../services/api';
import usePageTitle from '../../utils/usePageTitle';

const DeliveryRequest = () => {
  usePageTitle('Delivery Request');
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', items: [] });

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const searchCriteria = { page: paginationModel.page + 1, limit: paginationModel.pageSize };
      const data = await fetchDeliveryRequests(searchCriteria);
      const results = Array.isArray(data) ? data : (data?.results || data?.data || []);
      const pagination = data?.pagination;
      
      const formattedData = results.map((item, index) => ({
        id: item.id || item.projectCode || item.project_code || index,
        projectCode: item.projectCode || item.project_code,
        budgetCode: item.budgetCode || item.budget_code,
        requestorName: item.requestorName || item.requestor_name,
        personInCharge: item.personInCharge || item.person_in_charge,
        modelName: item.modelName || item.model_name,
        projectStatus: item.projectStatus || item.project_status,
        drModelNames: item.drModelNames || item.dr_model_names || ''
      }));
      
      setFilteredRows(formattedData);
      setTotalCount(pagination?.totalItems || results.length);
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load data';
      setError(`Error: ${errorMessage}`);
      setFilteredRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, [paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleSearch = async (searchCriteria) => {
    setLoading(true);
    setError(null);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    
    try {
      const searchParams = { ...searchCriteria, page: 1, limit: paginationModel.pageSize };
      const response = await searchProjects(searchParams);
      const results = Array.isArray(response) ? response : (response?.results || response?.data || []);
      const pagination = response?.pagination;
      
      const formattedData = results.map((item, index) => ({
        id: item.id || item.projectCode || item.project_code || index,
        projectCode: item.projectCode || item.project_code,
        budgetCode: item.budgetCode || item.budget_code,
        requestorName: item.requestorName || item.requestor_name,
        personInCharge: item.personInCharge || item.person_in_charge,
        modelName: item.modelName || item.model_name,
        projectStatus: item.projectStatus || item.project_status,
        drModelNames: item.drModelNames || item.dr_model_names || ''
      }));
      
      setFilteredRows(formattedData);
      setTotalCount(pagination?.totalItems || results.length);
    } catch (err) {
      setError('Failed to perform search. Please try again.');
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    setError(null);
    setTotalCount(0);
    setSelectedRows([]);
    loadInitialData();
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedRows(filteredRows.map(row => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) 
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const handleDeleteDR = () => {
    if (selectedRows.length === 0) return;
    const selectedItems = filteredRows.filter(row => selectedRows.includes(row.id));
    setConfirmDialog({ 
      open: true, 
      action: 'delete', 
      items: selectedItems 
    });
  };

  const handleTerminateProject = () => {
    if (selectedRows.length === 0) return;
    const selectedItems = filteredRows.filter(row => selectedRows.includes(row.id));
    setConfirmDialog({ 
      open: true, 
      action: 'terminate', 
      items: selectedItems 
    });
  };

  const confirmAction = async () => {
    try {
      setLoading(true);
      console.log(`${confirmDialog.action} action for:`, confirmDialog.items);
      
      // Extract project codes from selected items
      const projectCodes = confirmDialog.items.map(item => item.projectCode || item.id);
      
      let response;
      if (confirmDialog.action === 'terminate') {
        response = await api.patch('/delivery-request/terminate', { ids: projectCodes });
        console.log('Terminate response:', response.data);
      } else if (confirmDialog.action === 'delete') {
        response = await api.delete('/delivery-request/bulk', { data: { ids: projectCodes } });
        console.log('Delete response:', response.data);
      }
      
      // Check if operation was successful
      if (response?.data?.success === false) {
        const errors = response.data.errors || [response.data.message || 'Operation failed'];
        const errorMsg = errors.join('\n');
        setError(errorMsg);
        setConfirmDialog({ open: false, action: '', items: [] });
        return;
      }
      
      // Remove selected items from the current view
      setFilteredRows(prev => prev.filter(row => !selectedRows.includes(row.id)));
      setSelectedRows([]);
      setConfirmDialog({ open: false, action: '', items: [] });
      
      // Optionally reload data to get fresh state
      setTimeout(() => {
        loadInitialData();
      }, 1000);
      
    } catch (err) {
      console.error(`${confirmDialog.action} error:`, err);
      setError(`Failed to ${confirmDialog.action} items: ${err.message}`);
      setConfirmDialog({ open: false, action: '', items: [] });
    } finally {
      setLoading(false);
    }
  };

  const cancelAction = () => {
    setConfirmDialog({ open: false, action: '', items: [] });
  };





  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4" gutterBottom component="h1">
          Delivery Request
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteDR}
            disabled={selectedRows.length === 0}
            aria-label={`Delete ${selectedRows.length} selected delivery requests`}
          >
            Delete DR ({selectedRows.length})
          </Button>
          <Button 
            variant="contained" 
            color="warning" 
            onClick={handleTerminateProject}
            disabled={selectedRows.length === 0}
            aria-label={`Terminate ${selectedRows.length} selected projects`}
          >
            Terminate Project ({selectedRows.length})
          </Button>
        </Box>
      </Box>

      {loading && (
        <LinearProgress sx={{ mb: 2 }} />
      )}

      <SearchComponent
        onSearch={handleSearch}
        onReset={handleReset}
        title="Search Criteria"
      />

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            whiteSpace: 'pre-line',
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Box component="span" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            Unable to complete operation
          </Box>
          {error}
        </Alert>
      )}

      {!loading && filteredRows && Array.isArray(filteredRows) && (
        <Paper sx={{ width: '100%', mb: 2, boxShadow: 2, borderRadius: 2 }}>
          <TableContainer>
            <Table sx={{ minWidth: 750 }} aria-label="Delivery requests data">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', width: '40px', minWidth: '40px', padding: '10px 4px' }}>
                    <Checkbox
                      checked={selectedRows.length === filteredRows.length && filteredRows.length > 0}
                      indeterminate={selectedRows.length > 0 && selectedRows.length < filteredRows.length}
                      onChange={handleSelectAll}
                      inputProps={{ 'aria-label': 'Select all delivery requests' }}
                      sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                    />
                  </TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Project Code</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Budget Code</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Requestor Name</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Person In Charge</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Model Name</TableCell>
                  <TableCell component="th" scope="col" sx={{ color: 'white', fontWeight: 'bold', fontSize: '14px', padding: '10px 12px' }}>Project Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row, index) => (
                  <TableRow 
                    key={row.id || row.projectCode || index}
                    hover
                    selected={selectedRows.includes(row.id)}
                    sx={{
                      backgroundColor: selectedRows.includes(row.id) ? '#e3f2fd' :
                        row.projectStatus?.toLowerCase() === 'new' ? '#CFBEBE' : 
                        (index % 2 === 0 ? '#fafafa' : 'white'),
                      color: row.projectStatus?.toLowerCase() === 'new' ? 'black' : 'inherit',
                      '&:hover': { 
                        backgroundColor: row.projectStatus?.toLowerCase() === 'new' ? '#c4a8a8 !important' : '#e3f2fd !important' 
                      }
                    }}
                  >
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onChange={() => handleSelectRow(row.id)}
                        inputProps={{ 'aria-label': `Select delivery request ${row.projectCode}` }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                      {row.projectCode ? (
                        <Link
                          component={RouterLink}
                          to={`/delivery-request/${row.projectCode}`}
                          aria-label={`View details for delivery request ${row.projectCode}`}
                          sx={{ 
                            color: '#0d47a1',
                            textDecorationThickness: '2px',
                            fontSize: '13px',
                            '&:hover': { color: '#0a3d91' },
                            '&:focus-visible': {
                              outline: '3px solid #000000',
                              outlineOffset: '2px',
                              borderRadius: '2px',
                            }
                          }}
                        >
                          {row.projectCode}
                        </Link>
                      ) : ''}
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>{row.budgetCode || ''}</TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>{row.requestorName || ''}</TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>{row.personInCharge || ''}</TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>
                      <Tooltip 
                        title={
                          <div>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Models for {row.projectCode}:</div>
                            {row.drModelNames ? (
                              String(row.drModelNames).split('&amp;').filter(model => model.trim() && !model.includes('Please list it in new lines')).map((model, idx, arr) => (
                                <span key={idx}>{model.trim()}{idx < arr.length - 1 ? '; ' : ''}</span>
                              ))
                            ) : (
                              <div>No models available</div>
                            )}
                          </div>
                        }
                        placement="top"
                        arrow
                      >
                        <span style={{ cursor: 'help' }}>{row.modelName || ''}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', padding: '6px 12px' }}>{row.projectStatus || ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={paginationModel.pageSize}
            page={paginationModel.page}
            onPageChange={(event, newPage) => setPaginationModel({ ...paginationModel, page: newPage })}
            onRowsPerPageChange={(event) => setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) })}
            sx={{
              backgroundColor: '#f8f9fa',
              borderTop: '2px solid #e0e0e0',
              '& .MuiTablePagination-root': { color: '#666' }
            }}
          />
        </Paper>
      )}
      
      {loading && (
        <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      )}

      {initialized && !loading && Array.isArray(filteredRows) && filteredRows.length === 0 && !error && (
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body1" color="text.secondary">
            No results found. Please adjust your search criteria.
          </Typography>
        </Box>
      )}

      <Dialog open={confirmDialog.open} onClose={cancelAction}>
        <DialogTitle>
          {confirmDialog.action === 'delete' ? 'Delete Delivery Requests' : 'Terminate Projects'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to {confirmDialog.action} the following {confirmDialog.items.length} item(s)?
          </DialogContentText>
          {confirmDialog.items.map((item, index) => (
            <Typography key={index} variant="body2" sx={{ mt: 1 }}>
              • {item.projectCode} - {item.requestorName}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelAction}>Cancel</Button>
          <Button 
            onClick={confirmAction} 
            color={confirmDialog.action === 'delete' ? 'error' : 'warning'}
            variant="contained"
          >
            {confirmDialog.action === 'delete' ? 'Delete' : 'Terminate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeliveryRequest;